import { Edit2, Eye, Trash2, UserIcon } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Pagination from "../../../components/ui/dashboard/Pagination";
import Table, { Column } from "../../../components/ui/dashboard/Table";
import ThreeDotsMenu from "../../../components/ui/ThreeDotsMenu";
import { buildApiUrl, formatCurrency } from "../../../lib/utils";
import {
  FineHistory,
  FineHistorySearchRequest,
  FineHistorySearchResponse,
} from "../@types";
import { useGetFineHistoryMutation } from "../fineApiSlice";
import IssueFineModal from "../IssueFineModal";
import DeleteFineHistoryModal from "../ui/DeleteFineHistory";

const FineHistoryDashTab: React.FC = () => {
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState("");

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [selectedFineHistory, setSelectedFineHistory] =
    useState<FineHistory | null>(null);

  const [isUpdateFineHistoryModalOpen, setIsUpdateFineHistoryModalOpen] =
    useState(false);

  // RTK Query mutation
  const [getFineHistory, { data: fineHistoryData, isLoading, error: loadError }] =
    useGetFineHistoryMutation();

  const fetchFineHistory = useCallback(async () => {
    const requestBody: FineHistorySearchRequest = {
      pageNumber: currentPage,
      pageSize: pageSize,
    };

    try {
      await getFineHistory(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch fineHistory:", err);
    }
  }, [currentPage, pageSize, getFineHistory]);

  useEffect(() => {
    fetchFineHistory();
  }, [currentPage, pageSize, fetchFineHistory]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  // Table columns configuration
  const columns: Column<FineHistory>[] = [
    {
      key: "driverName",
      header: "Driver",
      width: "300px",
      render: (value: string, item: FineHistory) => (
        <div className="flex items-center space-x-3">
          {item.driverImageUrl ? (
            <img
              src={buildApiUrl(item.driverImageUrl)}
              alt={`${item.driverName}`}
              className="w-[30px] h-[30px] rounded-full object-cover border border-gray-300 shadow-sm"
            />
          ) : (
            <div className="w-[30px] h-[30px] rounded-full bg-gray-200 flex items-center justify-center border border-gray-300 shadow-sm p-1">
              <UserIcon className="w-full h-full text-hColor" />
            </div>
          )}
          <div className="flex flex-col">
            <span className="text-hColor capitalize">{item.driverName}</span>
          </div>
        </div>
      ),
    },
    {
      key: "fineName",
      header: "Fine issued",
      width: "250px",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "fineCategory",
      header: "Fine category",
      width: "250px",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "createdOn",
      header: "Date issued",
      width: "250px",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "fineAmount",
      header: "FineAmount",
      width: "250px",
      render: (value: number) => (
        <span className="text-hColor">{formatCurrency(value)}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      width: "12",
      align: "center" as const,
      render: (value: any, item: FineHistory) => {
        return (
          <ThreeDotsMenu
            actions={[
              {
                label: "Edit",
                onClick: () => {
                  setSelectedFineHistory(item);
                  setShowModal("edit");
                },
                icon: <Edit2 className="w-4 h-4" />,
              },
              {
                label: "View driver",
                onClick: () => navigate(`/admin/drivers/${item.driverId}`),
                icon: <Eye className="w-4 h-4" />,
              },
              {
                label: "Delete",
                onClick: () => {
                  setSelectedFineHistory(item);
                  setShowModal("delete");
                },
                icon: <Trash2 className="w-4 h-4" />,
              },
            ]}
          />
        );
      },
    },
  ];

  const handleClose = () => {
    fetchFineHistory();
    setShowModal("");
    setIsUpdateFineHistoryModalOpen(false);
  };

  const response = fineHistoryData?.data as
    | FineHistorySearchResponse
    | undefined;

  return (
    <div>
      <div className="space-y-6">
        {/* Table */}
        <div className="overflow-hidden space-y-6">
          <Table
            data={response?.data || []}
            columns={columns}
            loading={isLoading}
            error={loadError}
            emptyMessage="No fineHistory found"
          />

          {/* Pagination */}
          {response && (
            <Pagination
              currentPage={response.currentPage}
              totalPages={response.totalPages}
              pageSize={response.pageSize}
              totalCount={response.totalCount}
              hasNextPage={response.hasNextPage}
              hasPreviousPage={response.hasPreviousPage}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </div>
      </div>
      {selectedFineHistory && (
        <IssueFineModal
          isOpen={showModal === "edit"}
          onClose={handleClose}
          issueFine={selectedFineHistory}
          mode="edit"
        />
      )}

      {selectedFineHistory && (
        <DeleteFineHistoryModal
          isOpen={showModal === "delete"}
          onClose={handleClose}
          fine={selectedFineHistory}
        />
      )}
    </div>
  );
};

export default FineHistoryDashTab;
